import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider'
import { formatBytes } from '../lib/format'
import { MAIN_SITE } from '../lib/site'

const WEB_EXTS = [
  'html', 'htm', 'css', 'js', 'mjs', 'json', 'map', 'txt', 'md', 'webmanifest',
  'svg', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'ico', 'bmp',
  'woff', 'woff2', 'ttf', 'otf', 'eot',
  'mp3', 'mp4', 'webm', 'csv', 'xml',
]
const ACCEPT = WEB_EXTS.map((e) => `.${e}`).join(',')

/** 直传并发数 */
const UPLOAD_CONCURRENCY = 5

/** 上传整个文件夹时默认不勾选的目录名（仍可手动勾选上传） */
const JUNK_DIRS = new Set(['.git', 'node_modules', '.idea', '.vscode', '.sass-cache', '__macosx'])
/** 默认不勾选的系统杂项文件 */
const JUNK_FILES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini'])

function fileExtOf(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name)
  return m ? m[1].toLowerCase() : ''
}

/** 整文件夹上传时的默认排除规则：版本库/依赖目录、系统杂项、非网页资源 */
function autoExcluded(relPath: string): boolean {
  const segs = relPath.split('/')
  if (segs.slice(0, -1).some((s) => JUNK_DIRS.has(s.toLowerCase()))) return true
  const name = segs[segs.length - 1].toLowerCase()
  if (JUNK_FILES.has(name)) return true
  const ext = fileExtOf(name)
  if (!ext) return true
  return !WEB_EXTS.includes(ext)
}

interface StagedFile {
  id: string
  file: File
  /** 相对路径（文件夹拖入/选择时保留目录结构） */
  relPath: string
  /** 是否被排除（取消勾选）；排除的文件不参与额度、入口与上传 */
  excluded: boolean
}

interface FsEntryLike {
  isFile: boolean
  isDirectory: boolean
  name: string
  file?: (cb: (f: File) => void, err: (e: unknown) => void) => void
  createReader?: () => { readEntries: (cb: (entries: FsEntryLike[]) => void, err: (e: unknown) => void) => void }
}

async function walkEntry(entry: FsEntryLike, prefix: string, out: { file: File; rel: string }[]) {
  if (entry.isFile && entry.file) {
    const file = await new Promise<File>((resolve, reject) => entry.file!(resolve, reject))
    out.push({ file, rel: prefix + entry.name })
    return
  }
  if (entry.isDirectory && entry.createReader) {
    const reader = entry.createReader()
    // readEntries 单次最多返回 100 条，循环读到空为止
    for (;;) {
      const batch = await new Promise<FsEntryLike[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      )
      if (!batch.length) break
      await Promise.all(batch.map((e) => walkEntry(e, `${prefix}${entry.name}/`, out)))
    }
  }
}

async function filesFromDataTransfer(dt: DataTransfer): Promise<{ file: File; rel: string }[]> {
  const entries: FsEntryLike[] = []
  if (dt.items?.length) {
    for (const item of Array.from(dt.items)) {
      const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null
      if (entry) entries.push(entry as unknown as FsEntryLike)
    }
  }
  if (entries.length) {
    const out: { file: File; rel: string }[] = []
    await Promise.all(entries.map((e) => walkEntry(e, '', out)))
    return out
  }
  return Array.from(dt.files).map((f) => ({ file: f, rel: f.name }))
}

function fileRelPath(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath
  return rel || file.name
}

function basename(path: string): string {
  return path.split('/').pop() || path
}

function ext(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name)
  return m ? m[1] : '?'
}

let uid = 0
function nextId(): string {
  uid += 1
  return `f${uid}`
}

/* ---------- 暂存文件目录树 ---------- */

interface TreeDir {
  name: string
  /** 目录相对路径，根为 '' */
  path: string
  dirs: TreeDir[]
  files: StagedFile[]
  /** 目录下（含子目录）全部文件 */
  allFiles: StagedFile[]
  /** 目录整体被默认排除（如 node_modules）时默认收起 */
  suggestCollapse: boolean
}

/** 由暂存文件的相对路径构建目录树；根节点 path 为 ''，渲染时直接铺开其子项 */
function buildTree(files: StagedFile[]): TreeDir {
  const root: TreeDir = { name: '', path: '', dirs: [], files: [], allFiles: [], suggestCollapse: false }
  const dirIndex = new Map<string, TreeDir>([['', root]])
  const ensureDir = (path: string): TreeDir => {
    const found = dirIndex.get(path)
    if (found) return found
    const i = path.lastIndexOf('/')
    const parent = ensureDir(i === -1 ? '' : path.slice(0, i))
    const dir: TreeDir = { name: path.slice(i + 1), path, dirs: [], files: [], allFiles: [], suggestCollapse: false }
    parent.dirs.push(dir)
    dirIndex.set(path, dir)
    return dir
  }
  for (const s of files) {
    const i = s.relPath.lastIndexOf('/')
    ensureDir(i === -1 ? '' : s.relPath.slice(0, i)).files.push(s)
  }
  const seal = (d: TreeDir): StagedFile[] => {
    d.dirs.sort((a, b) => a.name.localeCompare(b.name))
    d.files.sort((a, b) => a.relPath.localeCompare(b.relPath))
    d.allFiles = [...d.files]
    for (const sub of d.dirs) d.allFiles.push(...seal(sub))
    // 只看路径规则而非当前勾选状态，避免用户勾选时目录突然折叠
    d.suggestCollapse = d.allFiles.length > 30 && d.allFiles.every((f) => autoExcluded(f.relPath))
    return d.allFiles
  }
  seal(root)
  return root
}

function collectDirPaths(d: TreeDir, out: string[] = []): string[] {
  for (const sub of d.dirs) {
    out.push(sub.path)
    collectDirPaths(sub, out)
  }
  return out
}

/* ---------- 上传链路类型 ---------- */

interface Credential {
  upload_id: string
  upload_url: string
  object_key: string
  bytes: number
  content_type: string
}

interface SavedFile {
  rel_path: string
  object_key: string
  bytes: number
  content_type: string
}

/** 统一上传失败：code 对应 i18n 的 upload.{code} */
interface UploadFail {
  fail: true
  code: string
  params?: Record<string, string>
}

class CancelledError extends Error {}

function fail(code: string, params?: Record<string, string>): UploadFail {
  return { fail: true, code, params }
}

type Phase =
  | { k: 'idle' }
  | { k: 'checking' }
  | { k: 'uploading'; loaded: number; total: number; current: string; active: number }
  | { k: 'saving' }
  | { k: 'done'; projectId: string }
  | { k: 'error'; err: UploadFail }

/** 并发上传时同时存活的 XHR 集合：取消时全部中止 */
interface XhrRegistry {
  add(xhr: XMLHttpRequest): void
  remove(xhr: XMLHttpRequest): void
}

/** XHR 直传（fetch 拿不到上传进度）；Content-Type 与凭证一致、大小由请求体本身保证 */
function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (loaded: number) => void,
  registry: XhrRegistry,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    registry.add(xhr)
    const settle = () => registry.remove(xhr)
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (e) => onProgress(e.loaded)
    xhr.onload = () => {
      settle()
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(fail('putFailed', { name: basename(file.name) }))
    }
    xhr.onabort = () => {
      settle()
      reject(new CancelledError())
    }
    xhr.onerror = () => {
      settle()
      reject(fail('networkError'))
    }
    xhr.send(file)
  })
}

async function readError(res: Response, filename: string): Promise<UploadFail> {
  const data = (await res.json().catch(() => ({}))) as {
    error?: string
    remaining_bytes?: number
    need_bytes?: number
  }
  switch (data.error) {
    case 'quota_exceeded':
      return fail('quotaExceeded', {
        remaining: formatBytes(data.remaining_bytes ?? 0),
        need: formatBytes(data.need_bytes ?? 0),
      })
    case 'upload_not_enabled':
      return fail('uploadNotEnabled')
    case 'invalid_request':
      return fail('fileTooLarge', { name: filename })
    case 'invalid_token':
    case 'insufficient_scope':
    case 'unauthorized':
      return fail('reauthNeeded')
    default:
      return fail('networkError')
  }
}

interface DropzoneProps {
  /** 覆盖更新模式：目标项目 id；提供后保存走 PUT /api/projects/{id} */
  projectId?: string
  /** 更新模式展示名（项目名/入口文件名） */
  projectTitle?: string
}

export function Dropzone({ projectId, projectTitle }: DropzoneProps) {
  const { t } = useTranslation()
  const { user, loading, login } = useAuth()
  const [dragOver, setDragOver] = useState(false)
  const [staged, setStaged] = useState<StagedFile[]>([])
  const [entryId, setEntryId] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>({ k: 'idle' })
  const [loginPromptOpen, setLoginPromptOpen] = useState(false)
  /** 目录折叠覆盖：path -> 是否收起；未覆盖时用 suggestCollapse 默认值 */
  const [dirCollapsed, setDirCollapsed] = useState<Record<string, boolean>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dirInputRef = useRef<HTMLInputElement>(null)
  const xhrsRef = useRef<Set<XMLHttpRequest>>(new Set())
  const cancelRef = useRef(false)

  // Esc 关闭登录提示弹窗
  useEffect(() => {
    if (!loginPromptOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLoginPromptOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [loginPromptOpen])

  const addFiles = (items: { file: File; rel: string }[]) => {
    if (!items.length) return
    setStaged((prev) => {
      const seen = new Set(prev.map((s) => `${s.relPath}:${s.file.size}`))
      const next = [...prev]
      for (const it of items) {
        const key = `${it.rel}:${it.file.size}`
        if (seen.has(key)) continue
        seen.add(key)
        next.push({ id: nextId(), file: it.file, relPath: it.rel, excluded: autoExcluded(it.rel) })
      }
      return next
    })
    setDirCollapsed({})
  }

  // 未登录时锁定选文件入口：点击/拖入只弹登录提示，不暂存（loading 期间放行，避免误判已登录用户）
  const locked = !user && !loading

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (locked) {
      setLoginPromptOpen(true)
      return
    }
    addFiles(await filesFromDataTransfer(e.dataTransfer))
  }

  /** 勾选（未排除）的文件：额度、入口页与上传都只看这些 */
  const included = useMemo(() => staged.filter((s) => !s.excluded), [staged])

  const htmlCandidates = useMemo(
    () => included.filter((s) => /\.html?$/i.test(s.relPath)),
    [included],
  )

  // 入口页默认 index.html，否则第一个 html
  useEffect(() => {
    if (!htmlCandidates.length) {
      setEntryId(null)
      return
    }
    const preferred =
      htmlCandidates.find((s) => /(^|\/)index\.html?$/i.test(s.relPath)) ?? htmlCandidates[0]
    if (!entryId || !htmlCandidates.some((s) => s.id === entryId)) {
      setEntryId(preferred.id)
    }
  }, [htmlCandidates, entryId])

  const totalBytes = useMemo(() => included.reduce((n, s) => n + s.file.size, 0), [included])

  const tree = useMemo(() => buildTree(staged), [staged])

  const autoSkipped = useMemo(
    () => staged.filter((s) => s.excluded && autoExcluded(s.relPath)).length,
    [staged],
  )
  const entryFile = useMemo(
    () => included.find((s) => s.id === entryId) ?? htmlCandidates[0] ?? null,
    [included, entryId, htmlCandidates],
  )

  const busy = phase.k === 'checking' || phase.k === 'uploading' || phase.k === 'saving'

  async function checkQuota(total: number): Promise<void> {
    const res = await fetch('/api/storage/quota', { credentials: 'same-origin' })
    if (res.status === 401 || res.status === 403) throw fail('reauthNeeded')
    if (!res.ok) throw fail('networkError')
    const quota = (await res.json()) as { remaining_bytes?: number }
    const remaining = quota.remaining_bytes ?? 0
    if (remaining < total) {
      throw fail('quotaExceeded', {
        remaining: formatBytes(remaining),
        need: formatBytes(total - remaining),
      })
    }
  }

  async function getCredential(s: StagedFile): Promise<Credential> {
    const res = await fetch('/api/uploads/credential', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        filename: basename(s.relPath),
        bytes: s.file.size,
        ...(s.file.type ? { content_type: s.file.type } : {}),
      }),
    })
    if (!res.ok) throw await readError(res, basename(s.relPath))
    return (await res.json()) as Credential
  }

  async function saveProject(files: SavedFile[]): Promise<string> {
    const res = await fetch(projectId ? `/api/projects/${projectId}` : '/api/projects', {
      method: projectId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        entry_path: entryFile!.relPath,
        files,
      }),
    })
    if (!res.ok) {
      if (projectId && (res.status === 404 || res.status === 403)) throw fail('updateNotFound')
      throw fail(projectId ? 'updateFailed' : 'saveFailed')
    }
    const data = (await res.json()) as { id: string }
    return data.id
  }

  async function startUpload() {
    if (!entryFile) return
    cancelRef.current = false
    setPhase({ k: 'checking' })
    try {
      await checkQuota(totalBytes)

      const files = included
      const saved: SavedFile[] = []
      const order = new Map(files.map((s, i) => [s.relPath, i]))
      const loadedMap = new Map<string, number>()
      const activeNames = new Set<string>()
      let firstError: unknown = null
      let lastUiAt = 0
      const touchUi = (force = false) => {
        const now = Date.now()
        if (!force && now - lastUiAt < 100) return
        lastUiAt = now
        let loaded = 0
        for (const v of loadedMap.values()) loaded += v
        const [current] = activeNames
        setPhase({ k: 'uploading', loaded, total: totalBytes, current: current ?? '', active: activeNames.size })
      }

      const queue = [...files]
      const worker = async (): Promise<void> => {
        for (;;) {
          if (cancelRef.current || firstError) return
          const s = queue.shift()
          if (!s) return
          activeNames.add(s.relPath)
          try {
            touchUi(true)
            const cred = await getCredential(s)
            await putWithProgress(
              cred.upload_url,
              s.file,
              cred.content_type,
              (loaded) => {
                loadedMap.set(s.id, loaded)
                touchUi()
              },
              {
                add: (xhr) => xhrsRef.current.add(xhr),
                remove: (xhr) => xhrsRef.current.delete(xhr),
              },
            )
            loadedMap.set(s.id, s.file.size)
            saved.push({
              rel_path: s.relPath,
              object_key: cred.object_key,
              bytes: cred.bytes,
              content_type: cred.content_type,
            })
          } catch (e) {
            // 首个错误记录后本 worker 退出；其余 worker 领新任务前也会停止
            if (!firstError) firstError = e
            return
          } finally {
            activeNames.delete(s.relPath)
            touchUi(true)
          }
        }
      }

      const pool = Math.min(UPLOAD_CONCURRENCY, files.length)
      await Promise.all(Array.from({ length: pool }, () => worker()))
      if (firstError) throw firstError

      saved.sort((a, b) => (order.get(a.rel_path) ?? 0) - (order.get(b.rel_path) ?? 0))
      setPhase({ k: 'saving' })
      const projectId = await saveProject(saved)
      setPhase({ k: 'done', projectId })
    } catch (e) {
      if (e instanceof CancelledError) {
        setPhase({ k: 'error', err: fail('cancelled') })
      } else if (e && (e as UploadFail).fail) {
        setPhase({ k: 'error', err: e as UploadFail })
      } else {
        setPhase({ k: 'error', err: fail('networkError') })
      }
    }
  }

  function cancelUpload() {
    cancelRef.current = true
    for (const xhr of xhrsRef.current) xhr.abort()
    xhrsRef.current.clear()
  }

  function resetAll() {
    setStaged([])
    setEntryId(null)
    setDirCollapsed({})
    setPhase({ k: 'idle' })
  }

  function toggleFile(id: string) {
    setStaged((prev) => prev.map((s) => (s.id === id ? { ...s, excluded: !s.excluded } : s)))
  }

  /** 目录三态勾选：全选 -> 全不选；非全选 -> 全选 */
  function toggleDir(dir: TreeDir) {
    const allIncluded = dir.allFiles.every((f) => !f.excluded)
    const ids = new Set(dir.allFiles.map((f) => f.id))
    setStaged((prev) =>
      prev.map((s) => (ids.has(s.id) ? { ...s, excluded: allIncluded } : s)),
    )
  }

  function isDirCollapsed(d: TreeDir): boolean {
    return dirCollapsed[d.path] ?? d.suggestCollapse
  }

  function toggleDirCollapse(d: TreeDir) {
    setDirCollapsed((prev) => ({ ...prev, [d.path]: !isDirCollapsed(d) }))
  }

  function setAllDirs(collapsed: boolean) {
    const next: Record<string, boolean> = {}
    for (const p of collectDirPaths(tree)) next[p] = collapsed
    setDirCollapsed(next)
  }

  const renderFile = (s: StagedFile): React.ReactNode => {
    const e = ext(s.relPath)
    const isHtml = /\.html?$/i.test(s.relPath)
    const isEntry = isHtml && !s.excluded && entryFile?.id === s.id
    return (
      <li key={s.id}>
        <div className={`tree-row${s.excluded ? ' skipped' : ''}`}>
          <span className="tree-caret-spacer" aria-hidden="true" />
          <input
            type="checkbox"
            checked={!s.excluded}
            disabled={busy}
            aria-label={t('dropzone.includeFile', { name: s.relPath })}
            onChange={() => toggleFile(s.id)}
          />
          <span className={`file-ext${isHtml ? ' html' : ''}`}>{e}</span>
          <span className="name" title={s.relPath}>{basename(s.relPath)}</span>
          {isHtml && !s.excluded && !isEntry && (
            <button
              type="button"
              className="entry-set"
              disabled={busy}
              onClick={() => setEntryId(s.id)}
            >
              {t('dropzone.setEntry')}
            </button>
          )}
          {isEntry && <span className="entry-badge">{t('dropzone.entryBadge')}</span>}
          <span className="size">{formatBytes(s.file.size)}</span>
        </div>
      </li>
    )
  }

  const renderDir = (d: TreeDir): React.ReactNode => {
    const inc = d.allFiles.filter((f) => !f.excluded)
    const checked = inc.length === d.allFiles.length
    const indeterminate = inc.length > 0 && !checked
    const collapsed = isDirCollapsed(d)
    const countText = checked ? `${d.allFiles.length}` : `${inc.length}/${d.allFiles.length}`
    return (
      <li key={d.path}>
        <div
          className={`tree-row dir${checked || indeterminate ? '' : ' skipped'}`}
          title={t('dropzone.toggleFolder', { name: d.name })}
          onClick={(e) => {
            if (busy) return
            // 勾选框等交互元素自身的点击不触发折叠
            if ((e.target as HTMLElement).closest('input, button')) return
            toggleDirCollapse(d)
          }}
        >
          <button
            type="button"
            className={`tree-caret${collapsed ? '' : ' open'}`}
            aria-expanded={!collapsed}
            aria-label={t('dropzone.toggleFolder', { name: d.name })}
            disabled={busy}
            onClick={() => toggleDirCollapse(d)}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 4 4 4-4 4" />
            </svg>
          </button>
          <input
            type="checkbox"
            checked={checked}
            disabled={busy}
            aria-label={t('dropzone.includeDir', { name: d.path })}
            ref={(el) => {
              if (el) el.indeterminate = indeterminate
            }}
            onChange={() => toggleDir(d)}
          />
          <svg className="tree-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 5h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
          </svg>
          <span className="name folder" title={d.path}>{d.name}</span>
          <span className="size">
            {countText} · {formatBytes(inc.reduce((n, f) => n + f.file.size, 0))}
          </span>
        </div>
        {!collapsed && (
          <ul className="file-tree">
            {d.dirs.map(renderDir)}
            {d.files.map(renderFile)}
          </ul>
        )}
      </li>
    )
  }

  return (
    <div className="dropzone-wrap">
      <div
        className={`dropzone${dragOver ? ' dragover' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={t('dropzone.hint')}
        onClick={(e) => {
          // 隐藏 input 冒泡上来的点击不算“点了投放区”
          if ((e.target as HTMLElement).closest('input')) return
          locked ? setLoginPromptOpen(true) : fileInputRef.current?.click()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (locked) setLoginPromptOpen(true)
            else fileInputRef.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => void onDrop(e)}
      >
        <svg className="dz-icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 4h16l8 8v28a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" />
          <path d="M28 4v8h8" />
          <path d="M24 32v-10m0 0 5 5m-5-5-5 5" />
        </svg>
        <p className="dz-title">{t('dropzone.hint')}</p>
        <p className="dz-sub">{t('home.formats')}</p>
        <div
          className="dz-actions"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => (locked ? setLoginPromptOpen(true) : fileInputRef.current?.click())}
          >
            {t('dropzone.pickFiles')}
          </button>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => (locked ? setLoginPromptOpen(true) : dirInputRef.current?.click())}
          >
            {t('dropzone.pickFolder')}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="visually-hidden"
          aria-hidden="true"
          tabIndex={-1}
          /* 程序化 click() 会冒泡到 .dropzone 的 onClick 再开一次文件选择器，必须在 input 上截断 */
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []).map((f) => ({ file: f, rel: fileRelPath(f) })))
            e.target.value = ''
          }}
        />
        {/* webkitdirectory 直接写在 JSX（React 会透传未知小写属性），避免挂载后 setAttribute 的时序问题 */}
        <input
          ref={dirInputRef}
          type="file"
          multiple
          webkitdirectory=""
          directory=""
          className="visually-hidden"
          aria-hidden="true"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []).map((f) => ({ file: f, rel: fileRelPath(f) })))
            e.target.value = ''
          }}
        />
      </div>

      {staged.length > 0 && phase.k !== 'done' && (
        <div className="staged">
          {projectId && (
            <p className="warn-note">
              {t('dropzone.updateNote', { name: projectTitle ?? projectId })}
            </p>
          )}
          <div className="summary-row">
            <span>
              {t('dropzone.total', {
                count: included.length,
                all: staged.length,
                size: formatBytes(totalBytes),
              })}
            </span>
            <span className="summary-actions">
              {tree.dirs.length > 0 && (
                <>
                  <button type="button" className="btn ghost sm" onClick={() => setAllDirs(false)} disabled={busy}>
                    {t('dropzone.expandAll')}
                  </button>
                  <button type="button" className="btn ghost sm" onClick={() => setAllDirs(true)} disabled={busy}>
                    {t('dropzone.collapseAll')}
                  </button>
                </>
              )}
              <button type="button" className="btn ghost sm" onClick={resetAll} disabled={busy}>
                {t('dropzone.clear')}
              </button>
            </span>
          </div>

          {autoSkipped > 0 && <p className="hint-note">{t('dropzone.autoSkipped', { count: autoSkipped })}</p>}

          {included.length === 0 && <p className="warn-note">{t('dropzone.noSelected')}</p>}
          {included.length > 0 && htmlCandidates.length === 0 && (
            <p className="warn-note">{t('dropzone.noHtml')}</p>
          )}

          <ul className="file-tree tree-root">
            {tree.dirs.map(renderDir)}
            {tree.files.map(renderFile)}
          </ul>

          <div className="upload-row">
            <button
              type="button"
              className="btn primary lg"
              disabled={busy || !entryFile}
              onClick={() => {
                if (!user) {
                  setLoginPromptOpen(true)
                  return
                }
                void startUpload()
              }}
            >
              {projectId ? t('dropzone.updateBtn') : t('dropzone.uploadBtn')}
            </button>
          </div>

          {(phase.k === 'checking' || phase.k === 'saving') && (
            <div className="upload-status">
              <div className="row">
                <span>{phase.k === 'checking' ? t('upload.checking') : t('upload.saving')}</span>
              </div>
              <div className="spinner" style={{ margin: '10px auto', width: 18, height: 18 }} aria-hidden="true" />
            </div>
          )}

          {phase.k === 'uploading' && (
            <div className="upload-status" role="status" aria-live="polite">
              <div className="row">
                <span className="name" title={phase.current}>
                  {phase.active > 1
                    ? t('upload.uploadingMany', { name: basename(phase.current), count: phase.active })
                    : t('upload.uploading', { name: basename(phase.current) })}
                </span>
                <span>
                  {formatBytes(phase.loaded)} / {formatBytes(phase.total)}
                </span>
                <button type="button" className="btn ghost sm" onClick={cancelUpload}>
                  {t('upload.cancel')}
                </button>
              </div>
              <div className="bar">
                <span style={{ width: `${phase.total ? Math.min(100, (phase.loaded / phase.total) * 100) : 0}%` }} />
              </div>
            </div>
          )}

          {phase.k === 'error' && (
            <div className="notice error" role="alert">
              <span>{t(`upload.${phase.err.code}`, phase.err.params)}</span>
              <div className="actions">
                <button type="button" className="btn ghost sm" onClick={() => setPhase({ k: 'idle' })}>
                  {t('upload.retry')}
                </button>
                {phase.err.code === 'quotaExceeded' && (
                  <a className="btn primary sm" href={MAIN_SITE} target="_blank" rel="noreferrer">
                    {t('upload.buyQuota')}
                  </a>
                )}
                {phase.err.code === 'reauthNeeded' && (
                  <button type="button" className="btn primary sm" onClick={login}>
                    {t('auth.login')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {phase.k === 'done' && (
        <div className="success-card">
          <div className="success-icon" aria-hidden="true">✓</div>
          <h3>{projectId ? t('upload.updateSuccessTitle') : t('upload.successTitle')}</h3>
          <p>{projectId ? t('upload.updateSuccessDesc') : t('upload.successDesc')}</p>
          <div className="success-actions">
            <a className="btn primary" href={`/p/${phase.projectId}/`} target="_blank" rel="noreferrer">
              {t('upload.openPreview')}
            </a>
            <Link className="btn ghost" to="/records">
              {projectId ? t('upload.backToRecords') : t('upload.viewRecords')}
            </Link>
            <button type="button" className="btn ghost" onClick={resetAll}>
              {projectId ? t('upload.updateMore') : t('upload.uploadMore')}
            </button>
          </div>
        </div>
      )}

      {loginPromptOpen && (
        <div className="modal-overlay" onClick={() => setLoginPromptOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-prompt-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            </div>
            <h3 id="login-prompt-title">{t('auth.loginPromptTitle')}</h3>
            <p>{t('auth.loginPromptDesc')}</p>
            <div className="modal-actions">
              <button type="button" className="btn primary" autoFocus onClick={login}>
                {t('auth.login')}
              </button>
              <button type="button" className="btn ghost" onClick={() => setLoginPromptOpen(false)}>
                {t('auth.notNow')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
